import { SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import {
    Button,
    Col,
    Collapse,
    CollapseProps,
    Form,
    InputNumber,
    Row,
    Select,
} from 'antd';
import { handlePositiveNumberInputKeyDown } from '@/utils/common';
import {
    createHealthNutritionProfilesComboFood,
    getListAgeComboFood,
    getListGenderComboFood,
    getListPhysicalActivityComboFood,
    getListNutritionHealthStatusComboFood,
    getListNutritionStatusComboFood,
    getListNutritionStatusZScoreComboFood,
    getListNutritionStatus60to19ComboFood,
    getListHealthNutritionProfilesComboFood,
    getComboFoodDetail,
    watchListGenderComboFood,
    watchListAgeComboFood,
    watchListPhysicalActivityComboFood,
    watchListNutritionHealthStatusComboFood,
    watchListNutritionStatusComboFood,
    watchListNutritionStatusZScoreComboFood,
    watchListNutritionStatus60to19ComboFood,
    watchComboFoodLoading,
    watchHealthNutritionProfiles
} from '@/redux/slices/combo-food.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useEffect, useState } from 'react';
import { PermissionAllowMode, PermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';

export const ComboFoodManagementCreate = ({
    setCreated,
    setRecordData,
    created
}: {
    setCreated: React.Dispatch<React.SetStateAction<boolean>>;
    setRecordData: React.Dispatch<React.SetStateAction<DataMeal[]>>
    created: boolean;
}) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const listAge = useAppSelector(watchListAgeComboFood);
    const listGender = useAppSelector(watchListGenderComboFood);
    const listPhysicalActivityComboFood = useAppSelector(watchListPhysicalActivityComboFood);
    const listNutritionHealthStatusComboFood = useAppSelector(watchListNutritionHealthStatusComboFood);
    const listNutritionStatusComboFood = useAppSelector(watchListNutritionStatusComboFood);
    const listNutritionStatusZScoreComboFood = useAppSelector(watchListNutritionStatusZScoreComboFood);
    const listNutritionStatus60to19ComboFood = useAppSelector(watchListNutritionStatus60to19ComboFood);
    const loading = useAppSelector(watchComboFoodLoading);
    const listProfileComboFoodDetail = useAppSelector(watchHealthNutritionProfiles);
    const { mode, push } = useQuery();
    const [allowMode, setAllowMode] = useState<boolean>(false);
    const [disableZSCore, setDisableZSCore] = useState<boolean>(true);
    const [disableBMIDT60_19, setDisableBMIDT60_19] = useState<boolean>(true);
    const [disableBMIDT19, setDisableBMIDT19] = useState<boolean>(true);

    const data: DataMeal[] = [
        {
            mode: PermissionAllowMode.CREATE,
            structureID: 0,
            data: []
        },
    ]

    const onFinish = async (values: any) => {
        const listResultAction = await dispatch(getListHealthNutritionProfilesComboFood({ customFilter: values }));

        if (getListHealthNutritionProfilesComboFood.fulfilled.match(listResultAction)) {
            const listData = listResultAction.payload?.listData || [];
            if (listData.length === 1) {
                const existingId = listData[0]?.PROFILE_ID;

                const detailResultAction = await dispatch(getComboFoodDetail({ profileID: existingId }));
                if (getComboFoodDetail.fulfilled.match(detailResultAction)) {
                    push(`${PageURLs.ofComboFoodManagement()}?mode=${PermissionMode.EDIT}&comboFoodId=${existingId}`);
                    setAllowMode(true);
                    setCreated(true);
                    return;
                } else {
                    return;
                }
            }
        } else {
            return;
        }

        const resultAction = await dispatch(createHealthNutritionProfilesComboFood(values));
        if (createHealthNutritionProfilesComboFood.fulfilled.match(resultAction)) {
            setAllowMode(true);
            setCreated(true);
            setRecordData(data);
        } else {
            setCreated(false);
        }
    };

    const listAgeOptions = listAge.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listGenderOptions = listGender.map((item) => ({
        value: item.id,
        label: item.name
    }));

    const listPhysicalActivityComboFoodOptions = listPhysicalActivityComboFood.map((item) => ({
        value: item.id,
        label: item.name
    }));

    const listNutritionHealthStatusOptions = listNutritionHealthStatusComboFood.map((item) => ({
        value: item.healthStatusId,
        label: item.displayName
    }));

    const listNutritionStatusOptions = listNutritionStatusComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listNutritionStatusZScoreOptions = listNutritionStatusZScoreComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const listNutritionStatus60to19Options = listNutritionStatus60to19ComboFood.map((item) => ({
        value: item.id,
        label: item.description
    }));

    const chooseAge = (option: string) => {
        if (option == 'DT00'
            || option == 'DT00_DT06'
            || option == 'DT01'
            || option == 'DT02'
            || option == 'DT03'
            || option == 'DT03_DT04'
            || option == 'DT04'
            || option == 'DT05'
            || option == 'DT06'
        ) {
            setDisableZSCore(false);
        } else {
            form.setFieldsValue({ ZSCORE_60: undefined });
            setDisableZSCore(true);
        }
        if (option == 'DT07'
            || option == 'DT07_DT12'
            || option == 'DT08'
            || option == 'DT08_DT10'
            || option == 'DT09'
            || option == 'DT10'
            || option == 'DT12'
            || option == 'DT12_DT15'
            || option == 'DT11'
        ) {
            setDisableBMIDT60_19(false);
        } else {
            form.setFieldsValue({ BMI_60_MONTH_19_AGE: undefined });
            setDisableBMIDT60_19(true);
        }
        if (option == 'DT13'
            || option == 'DT13_DT14'
            || option == 'DT14'
            || option == 'DT15'
            || option == 'DT16'
            || option == 'DT17'
            || option == 'DT18'
            || option == 'DT19'
            || option == 'DT20'
        ) {
            setDisableBMIDT19(false);
        } else {
            form.setFieldsValue({ BMI_GE_19_AGE: undefined });
            setDisableBMIDT19(true);
        }
    };

    useEffect(() => {
        dispatch(getListAgeComboFood());
        dispatch(getListGenderComboFood());
        dispatch(getListPhysicalActivityComboFood());
        dispatch(getListNutritionHealthStatusComboFood());
        dispatch(getListNutritionStatusComboFood());
        dispatch(getListNutritionStatusZScoreComboFood());
        dispatch(getListNutritionStatus60to19ComboFood());
    }, [dispatch])

    useEffect(() => {
        form.resetFields();
        if (mode === PermissionMode.EDIT && listProfileComboFoodDetail) {
            form.setFieldsValue(listProfileComboFoodDetail);
            setAllowMode(true);
        } else if (mode === PermissionMode.CREATE && listProfileComboFoodDetail) {
            form.setFieldsValue(listProfileComboFoodDetail);
            setAllowMode(true);
        }
    }, [form, listProfileComboFoodDetail, mode]);

    const items: CollapseProps['items'] = [
        {
            key: '1',
            children: (
                <Form form={form} css={formStyles} labelCol={{ span: 8 }} onFinish={onFinish}>
                    {/* Hàng 1: Độ tuổi, Tình trạng sức khỏe, Hoạt động thể lực */}
                    <Row gutter={24} style={{ marginBottom: '1rem' }}>
                        <Col span={8}>
                            <Form.Item
                                style={{ alignItems: 'center' }}
                                label={<span
                                    css={css`white-space: normal;`}
                                >
                                    Độ tuổi<span style={{ color: 'red' }}>*</span>
                                </span>}
                                name='AGE'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                                rules={[{ required: true, message: 'Vui lòng chọn độ tuổi' }]}
                            >
                                <Select
                                    options={listAgeOptions}
                                    allowClear
                                    disabled={allowMode}
                                    onSelect={(option) => chooseAge(option)}
                                    onClear={() => {
                                        setDisableZSCore(true);
                                        setDisableBMIDT60_19(true);
                                        setDisableBMIDT19(true);
                                        form.setFieldsValue({
                                            ZSCORE_60: undefined,
                                            BMI_60_MONTH_19_AGE: undefined,
                                            BMI_GE_19_AGE: undefined
                                        });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={
                                    <span>
                                        Tình trạng sức khỏe
                                        <span style={{ color: 'red' }}> *</span>
                                    </span>
                                }
                                name='HEALTH_STATUS_IDS'
                                css={customLabel}
                                labelCol={{ span: 10 }}
                                rules={[{ required: true, message: 'Vui lòng chọn tình trạng sức khoẻ' }]}
                            >
                                <Select
                                    options={listNutritionHealthStatusOptions}
                                    mode='multiple'
                                    allowClear
                                    disabled={allowMode}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={
                                    <span>
                                        Hoạt động thể lực
                                        <span style={{ color: 'red' }}> *</span>
                                    </span>
                                }
                                name='PHYSICAL_ACTIVITY_LEVEL'
                                css={customLabel}
                                labelCol={{ span: 10 }}
                                rules={[{ required: true, message: 'Vui lòng chọn hoạt động thể lực' }]}
                            >
                                <Select
                                    options={listPhysicalActivityComboFoodOptions}
                                    disabled={allowMode}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Hàng 2: Tình trạng dinh dưỡng theo Zscore, BMI (60 tháng - 19 tuổi), BMI (>19 tuổi) */}
                    <Row gutter={24} style={{ marginBottom: '1rem' }}>
                        <Col span={8} css={inpStyle}>
                            <Form.Item
                                label={<span
                                    css={css`white-space: normal;`}
                                >
                                    Tình trạng dinh dưỡng theo Zscore<br />(&lt;60 tháng tuổi)<span style={{ color: 'red' }}>*</span>
                                </span>}
                                name='ZSCORE_60'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                                rules={disableZSCore ? [] : [{ required: true, message: 'Vui lòng chọn tình trạng dinh dưỡng' }]}
                            >
                                <Select
                                    options={listNutritionStatusZScoreOptions}
                                    disabled={allowMode || disableZSCore}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} css={inpStyle}>
                            <Form.Item
                                label={<span
                                    css={css`white-space: normal;`}
                                >
                                    Tình trạng dinh dưỡng theo BMI<br />(60 tháng - 19 tuổi)<span style={{ color: 'red' }}> *</span>
                                </span>}
                                name='BMI_60_MONTH_19_AGE'
                                css={customLabel}
                                labelCol={{ span: 10 }}
                                rules={disableBMIDT60_19 ? [] : [{ required: true, message: 'Vui lòng chọn tình trạng dinh dưỡng' }]}
                            >
                                <Select
                                    options={listNutritionStatus60to19Options}
                                    disabled={allowMode || disableBMIDT60_19}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8} css={inpStyle}>
                            <Form.Item
                                label={<span
                                    css={css`white-space: normal;`}
                                >
                                    Tình trạng dinh dưỡng theo BMI<br />(&gt;19 tuổi)<span style={{ color: 'red' }}> *</span>
                                </span>}
                                name='BMI_GE_19_AGE'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                                rules={disableBMIDT19 ? [] : [{ required: true, message: 'Vui lòng chọn tình trạng dinh dưỡng' }]}
                            >
                                <Select
                                    options={listNutritionStatusOptions}
                                    disabled={allowMode || disableBMIDT19}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Hàng 3: Giới tính, Cân nặng */}
                    <Row gutter={24}>
                        <Col span={8}>
                            <Form.Item
                                style={{ lineHeight: "3.2rem" }}
                                label={
                                    <span>
                                        Giới tính
                                        <span style={{ color: 'red' }}> *</span>
                                    </span>
                                }
                                name='GENDER_ID'
                                css={customLabel}
                                labelCol={{ span: 8 }}
                                rules={[{ required: true, message: 'Vui lòng chọn giới tính' }]}
                            >
                                <Select
                                    options={listGenderOptions}
                                    disabled={allowMode}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                style={{ lineHeight: "3.2rem" }}
                                label={
                                    <span>
                                        Cân nặng
                                        <span style={{ color: 'red' }}> *</span>
                                    </span>
                                }
                                css={customLabel}
                                labelCol={{ span: 8 }}>
                                <Row gutter={8} align="middle">
                                    <Col span={11}>
                                        <Form.Item name="WEIGHT_MIN"
                                            style={{ marginBottom: 0 }}
                                            rules={[
                                                { required: true, message: 'Vui lòng nhập cân nặng' }
                                            ]}
                                        >
                                            <InputNumber
                                                type='number'
                                                min={0}
                                                style={{ width: '100%' }}
                                                disabled={allowMode}
                                                onKeyDown={handlePositiveNumberInputKeyDown}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={2} style={{ textAlign: "center" }}>-</Col>
                                    <Col span={11}>
                                        <Form.Item name="WEIGHT_MAX" style={{ marginBottom: 0 }}
                                            rules={[
                                                { required: true, message: 'Vui lòng nhập cân nặng' }
                                            ]}
                                        >
                                            <InputNumber
                                                type='number'
                                                min={0}
                                                style={{ width: '100%' }}
                                                disabled={allowMode}
                                                onKeyDown={handlePositiveNumberInputKeyDown}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form.Item>
                        </Col>
                    </Row>
                    {
                        !created && (
                            <Row
                                gutter={16}
                                align='middle'
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginTop: '1rem'
                                }}
                            >
                                <Button
                                    type='primary'
                                    icon={<SearchOutlined />}
                                    css={searchButton}
                                    loading={loading}
                                    htmlType='submit'
                                >
                                    Tạo thực đơn
                                </Button>
                            </Row>
                        )
                    }
                </Form >
            )
        }
    ];
    return (
        <div css={rootStyles}>
            <Row css={boxStyles} justify='space-between' align='middle'>
                <Collapse
                    items={items}
                    expandIcon={() => null}
                    css={collapseStyles}
                    activeKey={['1']}
                />
            </Row>
        </div>
    );
};

const boxStyles = css`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const collapseStyles = css`
  width: 100%;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  background-color: transparent !important;

  .ant-collapse-header {
    width: fit-content;
    border-radius: 0.8rem !important;
    border: none !important;
    padding: 0 !important;

    .ant-collapse-expand-icon {
      align-self: center;
    }
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-content {
    border: none !important;
  }
`;


const formStyles = css`
  margin-top: 0.5rem;
  padding: 1rem;
  .ant-form-item-label  {
    text-align: start;
  }
`;

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
`;

const customLabel = css`
  .ant-form-item-label > label {
    font-weight: normal !important;
    height: fit-content;

    &::after,
    &::before {
      display: none !important;
    }

    margin: 0 2rem 0 0 !important;
  }

  width: 100%;
  margin-bottom: 0;
`;

const searchButton = css`
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
`;

const inpStyle = css`
   min-height: 6.5rem;
   .ant-form-item-control-input {
    min-height: 6.5rem;
   }
`;